import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Admin confirmation dialog without window.confirm().
 * Render in document.body so fixed overlays are never clipped by a scrollable panel.
 */
export default function ConfirmDialog({
  open, title, description, details, confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler', busy = false, intent = 'default', onConfirm, onCancel,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the primary action only after the dialog appears.
    (intent === 'danger' ? cancelButtonRef : confirmRef).current?.focus();

    function handleKey(event) {
      if (event.key === 'Escape') {
        if (!busyRef.current) {
          event.preventDefault();
          cancelRef.current?.();
        }
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const elements = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      ));
      if (!elements.length) {
        event.preventDefault();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open, intent]);

  if (!open) return null;
  return createPortal(
    <div className="bo-confirm-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel?.();
      }}>
      <section className={`bo-confirm-dialog ${intent === 'danger' ? 'bo-confirm-dialog--danger' : ''}`} role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={descriptionId}
        aria-busy={busy} ref={dialogRef}>
        <div className="bo-confirm-dialog__icon" aria-hidden="true">{intent === 'danger' ? '!' : '✓'}</div>
        <h3 id={titleId}>{title}</h3>
        <p id={descriptionId} className="bo-muted">{description}</p>
        {details && <div className="bo-confirm-dialog__details">{details}</div>}
        <div className="bo-confirm-dialog__actions">
          <button type="button" className="bo-button bo-button--ghost"
            disabled={busy} ref={cancelButtonRef} onClick={onCancel}>{cancelLabel}</button>
          <button type="button" className={`bo-button ${intent === 'danger' ? 'bo-button--danger' : 'bo-button--primary'}`}
            disabled={busy} ref={confirmRef} onClick={onConfirm}>
            {busy ? 'Traitement en cours…' : confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}
